import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ArrowRight,
  CheckCircle,
  Target,
  Lightbulb,
  Users,
  Award,
  TrendingUp,
  Heart,
  Shield,
  Zap,
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: "Results-Driven",
      description: "Every strategy and system we build is designed to deliver measurable outcomes for your business.",
    },
    {
      icon: Heart,
      title: "Partnership",
      description: "We're not vendors—we're partners invested in your long-term success and growth.",
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "Honest communication, transparent pricing, and ethical practices in everything we do.",
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "We leverage cutting-edge tools and methodologies to stay ahead of the curve.",
    },
  ];

  const milestones = [
    { year: "2010", title: "Founded", description: "Started as a creative agency focused on video production" },
    { year: "2015", title: "Digital Pivot", description: "Expanded into digital marketing and automation" },
    { year: "2020", title: "Growth Partner", description: "Evolved into a full-service growth partner" },
    { year: "2024", title: "OptimaGrowth", description: "Launched OptimaGrowth with proprietary software" },
  ];

  return (
    <div className="pt-32" data-testid="about-page">
      <SEO 
        title="About OptimaGrowth | Cape Town Marketing Automation Experts"
        description="Meet the OptimaGrowth team — 15+ years of marketing expertise based in Cape Town, South Africa. We build automated growth systems for businesses in SA, UK & USA."
        keywords="about OptimaGrowth, Rudi van Zyl, marketing automation agency Cape Town, SEO growth partner, digital marketing South Africa"
      />
      <Breadcrumb items={[{ name: "About" }]} />
      {/* Hero Section */}
      <section className="section" data-testid="about-hero">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">About OptimaGrowth</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                Your Strategic SEO & Marketing Automation Partner
              </h1>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                OptimaGrowth is more than an agency—we're your dedicated growth partner. We combine strategic thinking, proven systems, and cutting-edge technology to help businesses scale sustainably.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Founded on the belief that every business deserves access to enterprise-level growth strategies, we've spent over 15 years refining our approach. Today, we help businesses across South Africa, the UK, and USA build systems that deliver consistent, measurable results.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="about-cta"
              >
                Start a Conversation <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800"
                alt="OptimaGrowth marketing automation agency team collaborating on SEO and growth strategy"
                className="rounded-2xl shadow-strong w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-alt" data-testid="mission-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-cyan mb-4">Our Mission</span>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
                Empowering Businesses to Scale Systematically
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                We exist to help ambitious businesses build the systems, processes, and strategies they need to achieve sustainable growth. Our mission is to democratize access to enterprise-level growth infrastructure—making it accessible, affordable, and actionable for businesses of all sizes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-blue mb-4">Our Values</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
              What We Stand For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-optima-blue/10 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-optima-blue" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-alt" data-testid="founder-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_optimascale/artifacts/khacej3k_Pappa%20Roots.png"
                alt="Rudi van Zyl - Founder of OptimaGrowth, SEO and marketing automation expert in South Africa"
                className="rounded-2xl shadow-medium w-full object-cover object-top"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-medium p-4">
                <div className="text-3xl font-heading font-bold text-optima-blue">15+</div>
                <div className="text-sm text-slate-500">Years Experience</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-pink mb-4">Meet the Founder</span>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
                Rudi van Zyl
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                With over 15 years of experience in marketing, technology, and creative strategy, Rudi founded OptimaGrowth to bridge the gap between ambitious businesses and the systems they need to scale.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Starting his career in video production and creative direction, Rudi developed a deep understanding of storytelling and brand building. As digital marketing evolved, he expanded into automation, CRM systems, and growth strategy—combining creativity with technology to deliver results.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Today, Rudi leads OptimaGrowth with a simple philosophy: <em>"Build systems that work while you sleep."</em> His approach focuses on sustainable growth through strategic automation, data-driven decisions, and genuine partnership with every client.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Award className="w-5 h-5 text-optima-blue" />
                  Certified Marketing Strategist
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <TrendingUp className="w-5 h-5 text-optima-blue" />
                  50+ Businesses Scaled
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section" data-testid="timeline-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-cyan mb-4">Our Journey</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
              How We Got Here
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex gap-6 items-start"
                >
                  <div className="flex-shrink-0 w-24 text-right">
                    <span className="font-heading text-2xl font-bold text-optima-blue">
                      {milestone.year}
                    </span>
                  </div>
                  <div className="flex-shrink-0 w-4 h-4 mt-2 rounded-full bg-optima-blue" />
                  <div className="flex-1 pb-8 border-b border-slate-200 last:border-0">
                    <h3 className="font-heading text-xl font-semibold text-optima-dark mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-slate-600">{milestone.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="about-cta-section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Let's Build Something Great Together
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Ready to partner with a team that's invested in your success? Let's talk about your growth goals.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="about-final-cta"
            >
              Start a Conversation <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Our Services</Link>
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing</Link>
              <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">See Case Studies</Link>
              <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
